import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import WhiteLogo from "./../../public/logo/colored_white.png";
import BlackLogo from "./../../public/logo/black.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const { theme, setTheme, resolvedTheme, systemTheme } = useTheme();
  const mobileMenuRef = useRef(null);
  const themeDropdownRef = useRef(null);
  const router = useRouter();

  // Check if current page is the about page
  const isAboutPage =
    router.pathname === "/about" || router.pathname === "/about-us";

  // Handle component mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      setIsScrolled(currentScrollPos > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        isMobileMenuOpen
      ) {
        setIsMobileMenuOpen(false);
      }

      if (
        themeDropdownRef.current &&
        !themeDropdownRef.current.contains(event.target) &&
        isThemeDropdownOpen
      ) {
        setIsThemeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen, isThemeDropdownOpen]);

  // Toggle between light and dark (no dropdown)
  const handleThemeToggle = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  // Mobile theme toggle - switch between light and dark
  const handleMobileThemeToggle = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  // Determine text color classes based on page, theme, and active state
  const getLinkClass = (href) => {
    const isActive = router.pathname === href;
    if (isActive) {
      return "text-accent dark:text-accent"; // Active state style
    }
    if (isAboutPage && resolvedTheme === "light" && !isScrolled) {
      return "text-white hover:text-accent";
    }
    return "text-grayish dark:text-white hover:text-accent dark:hover:text-accent";
  };

  // Determine mobile link classes
  const getMobileLinkClass = (href) => {
    const isActive = router.pathname === href;
    if (isActive) {
      return "text-accent dark:text-accent"; // Active state style for mobile
    }
    if (!isScrolled && isAboutPage && resolvedTheme === "light") {
      return "text-white hover:text-accent";
    }
    return "text-grayish dark:text-white hover:text-accent dark:hover:text-accent";
  };

  const getIconColorClass = () => {
    if (isAboutPage && resolvedTheme === "light" && !isScrolled) {
      return "text-white";
    }
    return "text-grayish dark:text-white";
  };

  const logoSrc = resolvedTheme === "dark" ? WhiteLogo : BlackLogo;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 dark:bg-jacarta-700/90 backdrop-blur-sm shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div ref={mobileMenuRef}>
        <div className="mx-auto px-4 sm:px-6 lg:px-32">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center flex-shrink-0">
              <div className="relative w-32 h-8 sm:w-40 sm:h-10">
                {mounted && (
                  <Image
                    src={logoSrc}
                    alt="Impactoverse Logo"
                    fill
                    className="object-contain"
                    sizes="(max-width: 640px) 128px, 160px"
                    priority
                  />
                )}
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link
                href="/campaigns"
                className={`text-sm lg:text-base font-medium transition-colors hover:scale-105 transform duration-200 ${getLinkClass("/campaigns")}`}
              >
                BROWSE CAMPAIGNS
              </Link>
              <Link
                href="/marketplace"
                className={`text-sm lg:text-base font-medium transition-colors hover:scale-105 transform duration-200 ${getLinkClass("/marketplace")}`}
              >
                IMPACT ART MARKETPLACE
              </Link>
              {/* <Link
                href="/about"
                className={`text-sm lg:text-base font-medium transition-colors hover:scale-105 transform duration-200 ${getLinkClass("/about")}`}
              >
                EXPLORE
              </Link> */}

              <Link
                href="/sign-in"
                className={`w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center rounded-full ${
                  isAboutPage && resolvedTheme === "light" && !isScrolled
                    ? "hover:bg-white/20"
                    : "dark:bg-transparent hover:bg-jacarta-200 dark:hover:bg-jacarta-600"
                } transition-all duration-200 hover:scale-110 transform`}
                aria-label="Account"
                tabIndex={0}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className={`w-4 h-4 lg:w-5 lg:h-5 ${getIconColorClass()}`}
                >
                  <path
                    d="M4.62 18.12C5.64 17.34 6.78 16.725 8.04 16.275C9.3 15.825 10.62 15.6 12 15.6C13.38 15.6 14.7 15.825 15.96 16.275C17.22 16.725 18.36 17.34 19.38 18.12C20.08 17.3 20.625 16.37 21.015 15.33C21.405 14.29 21.6 13.18 21.6 12C21.6 9.34 20.665 7.075 18.795 5.205C16.925 3.335 14.66 2.4 12 2.4C9.34 2.4 7.075 3.335 5.205 5.205C3.335 7.075 2.4 9.34 2.4 12C2.4 13.18 2.595 14.29 2.985 15.33C3.375 16.37 3.92 17.3 4.62 18.12ZM12 13.2C10.82 13.2 9.825 12.795 9.015 11.985C8.205 11.175 7.8 10.18 7.8 9C7.8 7.82 8.205 6.825 9.015 6.015C9.825 5.205 10.82 4.8 12 4.8C13.18 4.8 14.175 5.205 14.985 6.015C15.795 6.825 16.2 7.82 16.2 9C16.2 10.18 15.795 11.175 14.985 11.985C14.175 12.795 13.18 13.2 12 13.2ZM12 24C10.34 24 8.78 23.685 7.32 23.055C5.86 22.425 4.59 21.57 3.51 20.49C2.43 19.41 1.575 18.14 0.945 16.68C0.315 15.22 0 13.66 0 12C0 10.34 0.315 8.78 0.945 7.32C1.575 5.86 2.43 4.59 3.51 3.51C4.59 2.43 5.86 1.575 7.32 0.945C8.78 0.315 10.34 0 12 0C13.66 0 15.22 0.315 16.68 0.945C18.14 1.575 19.41 2.43 20.49 3.51C21.57 4.59 22.425 5.86 23.055 7.32C23.685 8.78 24 10.34 24 12C24 13.66 23.685 15.22 23.055 16.68C22.425 18.14 21.57 19.41 20.49 20.49C19.41 21.57 18.14 22.425 16.68 23.055C15.22 23.685 13.66 24 12 24ZM12 21.6C13.06 21.6 14.06 21.445 15 21.135C15.94 20.825 16.8 20.38 17.58 19.8C16.8 19.22 15.94 18.775 15 18.465C14.06 18.155 13.06 18 12 18C10.94 18 9.94 18.155 9 18.465C8.06 18.775 7.2 19.22 6.42 19.8C7.2 20.38 8.06 20.825 9 21.135C9.94 21.445 10.94 21.6 12 21.6ZM12 10.8C12.52 10.8 12.95 10.63 13.29 10.29C13.63 9.95 13.8 9.52 13.8 9C13.8 8.48 13.63 8.05 13.29 7.71C12.95 7.37 12.52 7.2 12 7.2C11.48 7.2 11.05 7.37 10.71 7.71C10.37 8.05 10.2 8.48 10.2 9C10.2 9.52 10.37 9.95 10.71 10.29C11.05 10.63 11.48 10.8 12 10.8Z"
                    fill="evenodd"
                  />
                </svg>
              </Link>

              {/* Advanced Theme Selector */}
              <div className="relative" ref={themeDropdownRef}>
                <button
                  onClick={handleThemeToggle}
                  className={`w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center rounded-full ${
                    isAboutPage && resolvedTheme === "light" && !isScrolled
                      ? "hover:bg-white/20"
                      : "dark:bg-transparent hover:bg-jacarta-200 dark:hover:bg-jacarta-600"
                  } transition-all duration-200 hover:scale-110 transform group relative`}
                  aria-label={`Current theme: ${theme === "system" ? `System (${systemTheme})` : theme} - Click to change`}
                  title={
                    theme === "system"
                      ? `System (${systemTheme})`
                      : theme === "light"
                        ? "Light mode"
                        : "Dark mode"
                  }
                  tabIndex={0}
                >
                  {mounted &&
                    (resolvedTheme === "light" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className={`w-4 h-4 lg:w-5 lg:h-5 ${getIconColorClass()} transition-transform duration-200 ${isThemeDropdownOpen ? "rotate-12" : ""}`}
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`lg:w-5 lg:h-5 transition-transform duration-200 ${isThemeDropdownOpen ? "rotate-12" : ""}`}
                      >
                        <path
                          d="M10 4V0H12V4H10ZM16.65 6.75L15.275 5.375L18.075 2.5L19.475 3.925L16.65 6.75ZM18 12V10H22V12H18ZM10 22V18H12V22H10ZM5.35 6.7L2.5 3.925L3.925 2.525L6.75 5.35L5.35 6.7ZM18.05 19.5L15.275 16.625L16.625 15.275L19.475 18.025L18.05 19.5ZM0 12V10H4V12H0ZM3.925 19.5L2.525 18.075L5.325 15.275L6.05 15.95L6.775 16.65L3.925 19.5ZM11 17C9.33333 17 7.91667 16.4167 6.75 15.25C5.58333 14.0833 5 12.6667 5 11C5 9.33333 5.58333 7.91667 6.75 6.75C7.91667 5.58333 9.33333 5 11 5C12.6667 5 14.0833 5.58333 15.25 6.75C16.4167 7.91667 17 9.33333 17 11C17 12.6667 16.4167 14.0833 15.25 15.25C14.0833 16.4167 12.6667 17 11 17Z"
                          fill="#FFDD55"
                        />
                      </svg>
                    ))}
                </button>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden flex items-center p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
              tabIndex={0}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className={`w-6 h-6 ${getIconColorClass()}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={
                    isMobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
                  }
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden ${
            isScrolled
              ? "bg-white dark:bg-jacarta-700"
              : isAboutPage && resolvedTheme === "light"
                ? "bg-jacarta-800"
                : "bg-white dark:bg-jacarta-700"
          } overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? "max-h-96" : "max-h-0"
          }`}
        >
          <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-3">
            <Link
              href="/campaigns"
              className={`block py-3 px-4 rounded-lg font-medium transition-all duration-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700/50 ${getMobileLinkClass("/campaigns")}`}
              onClick={() => setIsMobileMenuOpen(false)}
              tabIndex={isMobileMenuOpen ? 0 : -1}
            >
              BROWSE CAMPAIGNS
            </Link>
            <Link
              href="/marketplace"
              className={`block py-3 px-4 rounded-lg font-medium transition-all duration-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700/50 ${getMobileLinkClass("/marketplace")}`}
              onClick={() => setIsMobileMenuOpen(false)}
              tabIndex={isMobileMenuOpen ? 0 : -1}
            >
              IMPACT ART MARKETPLACE
            </Link>
            {/* <Link
              href="/about"
              className={`block py-3 px-4 rounded-lg font-medium transition-all duration-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700/50 ${getMobileLinkClass("/about")}`}
              onClick={() => setIsMobileMenuOpen(false)}
              tabIndex={isMobileMenuOpen ? 0 : -1}
            >
              EXPLORE
            </Link> */}

            {/* Mobile Theme Toggle */}
            <button
              onClick={handleMobileThemeToggle}
              className={`flex items-center justify-between w-full py-3 px-4 rounded-lg transition-all duration-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700/50 ${
                !isScrolled && isAboutPage && resolvedTheme === "light"
                  ? "text-white"
                  : "text-grayish dark:text-white"
              }`}
              aria-label="Toggle theme"
              tabIndex={isMobileMenuOpen ? 0 : -1}
            >
              <span className="font-medium">
                {mounted &&
                  (theme === "light"
                    ? "Light Mode"
                    : theme === "dark"
                      ? "Dark Mode"
                      : `System (${systemTheme || "Auto"})`)}
              </span>
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  !isScrolled && isAboutPage && resolvedTheme === "light"
                    ? "bg-white/20"
                    : "bg-jacarta-100 dark:bg-jacarta-700"
                }`}
              >
                {mounted &&
                  (theme === "light" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`${
                        !isScrolled && isAboutPage && resolvedTheme === "light"
                          ? "text-white"
                          : "text-grayish dark:text-white"
                      }`}
                    >
                      <path
                        d="M10 4V0H12V4H10ZM16.65 6.75L15.275 5.375L18.075 2.5L19.475 3.925L16.65 6.75ZM18 12V10H22V12H18ZM10 22V18H12V22H10ZM5.35 6.7L2.5 3.925L3.925 2.525L6.75 5.35L5.35 6.7ZM18.05 19.5L15.275 16.625L16.625 15.275L19.475 18.025L18.05 19.5ZM0 12V10H4V12H0ZM3.925 19.5L2.525 18.075L5.325 15.275L6.05 15.95L6.775 16.65L3.925 19.5ZM11 17C9.33333 17 7.91667 16.4167 6.75 15.25C5.58333 14.0833 5 12.6667 5 11C5 9.33333 5.58333 7.91667 6.75 6.75C7.91667 5.58333 9.33333 5 11 5C12.6667 5 14.0833 5.58333 15.25 6.75C16.4167 7.91667 17 9.33333 17 11C17 12.6667 16.4167 14.0833 15.25 15.25C14.0833 16.4167 12.6667 17 11 17Z"
                        fill="#FFDD55"
                      />
                    </svg>
                  ) : theme === "dark" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className={`w-4 h-4 ${
                        !isScrolled && isAboutPage && resolvedTheme === "light"
                          ? "text-white"
                          : "text-grayish dark:text-white"
                      }`}
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width={16}
                      height={16}
                      className={`fill-current ${
                        !isScrolled && isAboutPage && resolvedTheme === "light"
                          ? "text-white"
                          : "text-grayish dark:text-white"
                      }`}
                    >
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path d="M4 3h16a1 1 0 0 1 1 1v7H3V4a1 1 0 0 1 1-1zM3 13v7a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-7H3zm9 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                    </svg>
                  ))}
              </div>
            </button>

            {/* Mobile Account Link */}
            <Link
              href="/sign-in"
              className={`flex items-center justify-between w-full py-3 px-4 rounded-lg transition-all duration-200 hover:bg-jacarta-50 dark:hover:bg-jacarta-700/50 ${
                !isScrolled && isAboutPage && resolvedTheme === "light"
                  ? "text-white hover:text-accent"
                  : "text-grayish dark:text-white hover:text-accent dark:hover:text-accent"
              }`}
              aria-label="Account"
              tabIndex={isMobileMenuOpen ? 0 : -1}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="font-medium">Account</span>
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  !isScrolled && isAboutPage && resolvedTheme === "light"
                    ? "bg-white/20"
                    : "bg-jacarta-100 dark:bg-jacarta-700"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className={`w-4 h-4 ${
                    !isScrolled && isAboutPage && resolvedTheme === "light"
                      ? "text-white"
                      : "text-grayish dark:text-white"
                  }`}
                >
                  <path
                    d="M4.62 18.12C5.64 17.34 6.78 16.725 8.04 16.275C9.3 15.825 10.62 15.6 12 15.6C13.38 15.6 14.7 15.825 15.96 16.275C17.22 16.725 18.36 17.34 19.38 18.12C20.08 17.3 20.625 16.37 21.015 15.33C21.405 14.29 21.6 13.18 21.6 12C21.6 9.34 20.665 7.075 18.795 5.205C16.925 3.335 14.66 2.4 12 2.4C9.34 2.4 7.075 3.335 5.205 5.205C3.335 7.075 2.4 9.34 2.4 12C2.4 13.18 2.595 14.29 2.985 15.33C3.375 16.37 3.92 17.3 4.62 18.12ZM12 13.2C10.82 13.2 9.825 12.795 9.015 11.985C8.205 11.175 7.8 10.18 7.8 9C7.8 7.82 8.205 6.825 9.015 6.015C9.825 5.205 10.82 4.8 12 4.8C13.18 4.8 14.175 5.205 14.985 6.015C15.795 6.825 16.2 7.82 16.2 9C16.2 10.18 15.795 11.175 14.985 11.985C14.175 12.795 13.18 13.2 12 13.2ZM12 24C10.34 24 8.78 23.685 7.32 23.055C5.86 22.425 4.59 21.57 3.51 20.49C2.43 19.41 1.575 18.14 0.945 16.68C0.315 15.22 0 13.66 0 12C0 10.34 0.315 8.78 0.945 7.32C1.575 5.86 2.43 4.59 3.51 3.51C4.59 2.43 5.86 1.575 7.32 0.945C8.78 0.315 10.34 0 12 0C13.66 0 15.22 0.315 16.68 0.945C18.14 1.575 19.41 2.43 20.49 3.51C21.57 4.59 22.425 5.86 23.055 7.32C23.685 8.78 24 10.34 24 12C24 13.66 23.685 15.22 23.055 16.68C22.425 18.14 21.57 19.41 20.49 20.49C19.41 21.57 18.14 22.425 16.68 23.055C15.22 23.685 13.66 24 12 24ZM12 21.6C13.06 21.6 14.06 21.445 15 21.135C15.94 20.825 16.8 20.38 17.58 19.8C16.8 19.22 15.94 18.775 15 18.465C14.06 18.155 13.06 18 12 18C10.94 18 9.94 18.155 9 18.465C8.06 18.775 7.2 19.22 6.42 19.8C7.2 20.38 8.06 20.825 9 21.135C9.94 21.445 10.94 21.6 12 21.6ZM12 10.8C12.52 10.8 12.95 10.63 13.29 10.29C13.63 9.95 13.8 9.52 13.8 9C13.8 8.48 13.63 8.05 13.29 7.71C12.95 7.37 12.52 7.2 12 7.2C11.48 7.2 11.05 7.37 10.71 7.71C10.37 8.05 10.2 8.48 10.2 9C10.2 9.52 10.37 9.95 10.71 10.29C11.05 10.63 11.48 10.8 12 10.8Z"
                    fill="evenodd"
                  />
                </svg>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
